import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { ArticleCard } from '@/components/feature/ArticleCard';
import { getArticleBySlug, getArticlesByCategory, getHotArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return getArticlesByCategory(article.categoryId)
      .filter((a) => a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  const hotArticles = getHotArticles().slice(0, 5);

  if (!article) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground-900">文章未找到</h2>
          <Link to="/" className="mt-4 text-primary-600 hover:text-primary-700 inline-flex items-center gap-1">
            <i className="ri-arrow-left-line"></i>返回首页
          </Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    dateModified: article.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '黑料网',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.heiliaowang.com/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.heiliaowang.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 黑料网`}
        description={article.excerpt}
        keywords={`黑料网,${article.categoryName},${article.tags.join(',')},${article.title}`}
        canonicalUrl={`/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
        articlePublishedTime={article.publishedAt}
        articleModifiedTime={article.updatedAt}
        schema={[articleSchema]}
      />

      {/* Breadcrumb */}
      <div className="pt-20 bg-background-50 border-b border-background-200/70">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.categoryId}` },
              { label: article.title },
            ]}
          />
        </div>
      </div>

      {/* Article Content */}
      <article className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Main */}
            <div className="flex-1 min-w-0 max-w-4xl">
              {/* Tags */}
              <div className="flex items-center gap-2 mb-4">
                {article.isExclusive && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium bg-accent-500 text-white rounded-full">
                    <i className="ri-vip-crown-line"></i>独家爆料
                  </span>
                )}
                {article.isHot && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium bg-primary-500 text-white rounded-full">
                    <i className="ri-fire-line"></i>热门
                  </span>
                )}
                <Link
                  to={`/category/${article.categoryId}`}
                  className="px-3 py-1 text-sm bg-background-100 text-foreground-600 rounded-full hover:bg-primary-100 hover:text-primary-700 transition-colors"
                >
                  {article.categoryName}
                </Link>
              </div>

              {/* Title */}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight">
                {article.title}
              </h1>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 mt-6 pb-6 border-b border-background-200/70">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-user-line text-primary-600 text-sm"></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700">{article.author}</span>
                </div>
                <span className="text-sm text-foreground-500">
                  {new Date(article.publishedAt).toLocaleDateString('zh-CN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
                <span className="text-sm text-foreground-500 flex items-center gap-1">
                  <i className="ri-eye-line"></i>{(article.viewCount / 10000).toFixed(1)}万阅读
                </span>
                <span className="text-sm text-foreground-500 flex items-center gap-1">
                  <i className="ri-chat-3-line"></i>{article.commentCount}评论
                </span>
                <span className="text-sm text-foreground-500">{article.readTime}分钟阅读</span>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mt-6">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full border border-background-200"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Cover Image */}
              <div className="mt-6 rounded-xl overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.title} - 黑料网${article.categoryName}`}
                  className="w-full object-cover"
                />
              </div>

              {/* Content */}
              <div
                className="mt-8 prose prose-lg max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-950
                  prose-p:text-foreground-800 prose-p:leading-relaxed prose-p:text-base
                  prose-strong:text-foreground-950 prose-strong:font-semibold
                  [&_p]:mb-5 [&_p]:text-foreground-800 [&_p]:leading-loose [&_p]:text-base
                "
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Disclaimer */}
              <div className="mt-10 p-4 bg-background-100 border border-background-200/70 rounded-xl">
                <p className="text-xs text-foreground-500 leading-relaxed">
                  <strong className="text-foreground-700">免责声明：</strong>
                  本文内容来源于网络爆料及用户投稿，不代表黑料网立场。我们致力于传播真实信息，但无法对所有内容的真实性进行逐一核实。如涉及侵权或不实信息，请通过关于我们页面的联系方式与我们取得联系，我们将及时处理。
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="w-full lg:w-80 flex-shrink-0">
              {/* Hot Articles */}
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 mb-6 sticky top-24">
                <h3 className="font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-fire-line text-primary-500"></i>
                  热门爆料
                </h3>
                <div className="space-y-4">
                  {hotArticles.map((item, index) => (
                    <Link
                      key={item.id}
                      to={`/article/${item.slug}`}
                      className="flex gap-3 group"
                    >
                      <span className={`w-6 h-6 flex items-center justify-center rounded text-xs font-bold flex-shrink-0 ${
                        index === 0 ? 'bg-primary-500 text-white' :
                        index === 1 ? 'bg-primary-400 text-white' :
                        index === 2 ? 'bg-primary-300 text-primary-900' :
                        'bg-background-200 text-foreground-500'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="min-w-0">
                        <h4 className="text-sm text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                          {item.title}
                        </h4>
                        <span className="text-xs text-foreground-500 mt-1 block">
                          {(item.viewCount / 10000).toFixed(1)}万阅读
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div className="mt-14 pt-10 border-t border-background-200/70">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  相关爆料
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((item) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </>
  );
}